// A FEJLÉC „Új…" MENÜJE (a régi „Gyors műveletek" csempe utódja): a create-akció parancs, nem tartalom — ezért nem
// csempe, hanem a lap fejlécének állandó eleme. Desktopon `DropdownMenu` a `ListNewButton` gradiens-stílusával; mobilon
// ugyanezeket a célokat az `AppShell` FAB-ja viszi (`newActionsFab`). Jog nélküli cél nincs a menüben; ha egy cél sincs,
// menü sincs.
//
// A MENÜ CSAK NAVIGÁL: a létrehozás a modul saját felületén történik (ott van a form-validáció és az optimista
// mutation) — a kezdőlap nem duplikál űrlapot. A cél a lista-oldal create-sheetjének `?new=1` deep-linkje (jegy,
// megbeszélés) vagy külön create-lap (ügyfél, fiók, számla).
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { type ComponentType, type ReactNode, type SVGProps } from 'react';

import { navGroups } from '@/components/shared/nav-config';

export interface NewAction {
  key: 'client' | 'account' | 'ticket' | 'meeting' | 'invoice';
  to: string;
  search?: Record<string, string>;
}

/** Melyik oldalsó menüpont a cél modulja — az ikon INNEN jön, nem külön listából. */
const NEW_ACTION_MODULE: Record<NewAction['key'], string> = {
  client: '/clients',
  account: '/accounts',
  ticket: '/tickets',
  meeting: '/meetings',
  invoice: '/invoices',
};

/**
 * Az „Új…" menü ikonjai az OLDALSÓ MENÜ ikonjai (`nav-config`), útvonal szerint — egy forrás, hogy a kettő ne
 * csússzon el (élesben látva: külön listában öt ikonból négy eltért a menüétől).
 */
export const NEW_ACTION_ICONS = Object.fromEntries(
  (Object.keys(NEW_ACTION_MODULE) as NewAction['key'][]).map((key) => [
    key,
    navGroups.flatMap((g) => g.items).find((i) => i.to === NEW_ACTION_MODULE[key])?.icon ??
      QuestionMarkCircleIcon,
  ]),
) as Record<NewAction['key'], ComponentType<SVGProps<SVGSVGElement>>>;

/** A jog-szűrt cél-lista (a mai öt `*.manage`). */
export function newActions(can: (permission: string) => boolean): NewAction[] {
  return [
    ...(can('clients.manage') ? [{ key: 'client' as const, to: '/clients/new' }] : []),
    ...(can('tickets.manage')
      ? [{ key: 'ticket' as const, to: '/tickets', search: { new: '1' } }]
      : []),
    ...(can('meetings.manage')
      ? [{ key: 'meeting' as const, to: '/meetings', search: { new: '1' } }]
      : []),
    ...(can('invoices.manage') ? [{ key: 'invoice' as const, to: '/invoices/new' }] : []),
    ...(can('accounts.manage') ? [{ key: 'account' as const, to: '/accounts/new' }] : []),
  ];
}

/** A mobil FAB másodlagos akciói ugyanezekből a célokból. */
export function newActionsFab(
  actions: NewAction[],
  label: (key: NewAction['key']) => string,
  go: (action: NewAction) => void,
): { key: string; icon: ReactNode; label: string; onClick: () => void }[] {
  return actions.map((a) => {
    const Icon = NEW_ACTION_ICONS[a.key];
    return { key: `new-${a.key}`, icon: <Icon />, label: label(a.key), onClick: () => go(a) };
  });
}
